import React, { useState, useEffect } from "react";
import axios from "axios";
import SyncConstants from "../../constants/SyncConstants";
import useAuthentication from "../../hooks/useAuthentication";
import { toast } from "react-hot-toast";
import { Activities } from "../../constants/ExpenseConstants";

const UserEditModal = ({ user, onClose }) => {
    const Auth = useAuthentication();
    const isEditMode = user !== null;

    const [formData, setFormData] = useState({
        username: user?.username || "",
        email: user?.email || "",
        real_name: user?.real_name || "",
        role: user?.role || "user",
        status: user?.status || "active",
        user_group: user?.user_group || "",
        has_slots: user?.has_slots !== undefined ? user.has_slots : true,
        has_calendar: user?.has_calendar !== undefined ? user.has_calendar : true,
        default_activity: user?.default_activity || "",
        password: ""
    });

    const [saving, setSaving] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? (checked ? 1 : 0) : value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSaving(true);

        // Validation
        if (!formData.username || !formData.email || !formData.real_name) {
            toast.error("Bitte fülle alle Pflichtfelder aus");
            setSaving(false);
            return;
        }

        if (!isEditMode && !formData.password) {
            toast.error("Passwort ist erforderlich für neue Benutzer");
            setSaving(false);
            return;
        }

        const dataToSend = { ...formData };

        // Remove password if empty (for edit mode)
        if (isEditMode && !formData.password) {
            delete dataToSend.password;
        }

        const endpoint = isEditMode
            ? SyncConstants["syncBaseURL"] + "user/update"
            : SyncConstants["syncBaseURL"] + "user/create";

        const params = isEditMode ? { id: user.id } : {};

        axios({
            method: "post",
            auth: Auth.getAuthObject(),
            url: endpoint,
            params: params,
            data: dataToSend
        }).then((response) => {
            toast.success(isEditMode ? "Benutzer aktualisiert" : "Benutzer erstellt");
            onClose(true); // true = reload users list
        }).catch((error) => {
            const errorMsg = error.response?.data?.error || "Fehler beim Speichern";
            toast.error(errorMsg);
            setSaving(false);
        });
    };

    return (
        <div className="overlay d-flex justify-content-center align-items-center">
            <div className="modal d-block">
                <div className="modal-dialog modal-lg">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {isEditMode ? "Benutzer bearbeiten" : "Neuer Benutzer"}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => onClose(false)}
                                disabled={saving}
                            ></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Benutzername *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="username"
                                            value={formData.username}
                                            onChange={handleChange}
                                            required
                                            disabled={isEditMode}
                                        />
                                        {isEditMode && (
                                            <small className="text-muted">
                                                Benutzername kann nicht geändert werden
                                            </small>
                                        )}
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">E-Mail *</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Vollständiger Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="real_name"
                                            value={formData.real_name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">
                                            {isEditMode ? "Neues Passwort" : "Passwort *"}
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            required={!isEditMode}
                                        />
                                        {isEditMode && (
                                            <small className="text-muted">
                                                Leer lassen, um Passwort nicht zu ändern
                                            </small>
                                        )}
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Rolle</label>
                                        <select
                                            className="form-select"
                                            name="role"
                                            value={formData.role}
                                            onChange={handleChange}
                                        >
                                            <option value="user">Benutzer</option>
                                            <option value="administrator">Administrator</option>
                                        </select>
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-select"
                                            name="status"
                                            value={formData.status}
                                            onChange={handleChange}
                                        >
                                            <option value="active">Aktiv</option>
                                            <option value="inactive">Inaktiv</option>
                                        </select>
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Team/Gruppe</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="user_group"
                                            value={formData.user_group}
                                            onChange={handleChange}
                                            placeholder="z.B. Team 2, UX"
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Standard-Aktivität</label>
                                        <select
                                            className="form-select"
                                            name="default_activity"
                                            value={formData.default_activity}
                                            onChange={handleChange}
                                        >
                                            <option value="">Keine Standard-Aktivität</option>
                                            {Object.entries(Activities).map(([key, label]) => (
                                                <option key={key} value={key}>{label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                name="has_slots"
                                                id="has_slots"
                                                checked={formData.has_slots}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor="has_slots">
                                                Hat Zugriff auf Slots
                                            </label>
                                        </div>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                name="has_calendar"
                                                id="has_calendar"
                                                checked={formData.has_calendar}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor="has_calendar">
                                                Hat Zugriff auf Kalender
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => onClose(false)}
                                    disabled={saving}
                                >
                                    Abbrechen
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Speichern...
                                        </>
                                    ) : (
                                        isEditMode ? "Änderungen speichern" : "Benutzer erstellen"
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserEditModal;
